import React from 'react';
import { Trophy, Calendar, DollarSign, Building, Edit, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const statusStyle = (status) => {
  switch (status) {
    case 'open':    return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    case 'awarded': return { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info };
    case 'closed':  return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
    default:        return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  }
};

const getDeadlineInfo = (deadline) => {
  const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
  if (days < 0)   return { label: 'Overdue', bg: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger };
  if (days <= 7)  return { label: 'Urgent',  bg: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger };
  if (days <= 30) return { label: 'Soon',    bg: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
  return           { label: 'Open',   bg: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
};

const CompetitionCard = ({ competition, onEdit, onCreateApplication, canEdit, canCreateApplication }) => {
  const dl = getDeadlineInfo(competition.applicationDeadline);

  return (
    <div
      className="flex flex-col rounded-2xl border transition-shadow hover:shadow-lg"
      style={{
        backgroundColor: SYSTEM_COLORS.shellSurface,
        borderColor: SYSTEM_COLORS.borderSoft,
        boxShadow: SYSTEM_COLORS.shadowSoft,
      }}
    >
      {/* Brand accent stripe */}
      <div className="h-1.5 rounded-t-2xl" style={{ backgroundColor: BRAND_COLORS.primary }} />

      <div className="flex flex-col flex-1 p-5">
        {/* Title row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg"
              style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
            >
              <Trophy className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
            </div>
            <h3
              className="text-sm font-semibold leading-snug line-clamp-2"
              style={{ color: NEUTRAL_COLORS.black }}
            >
              {competition.name}
            </h3>
          </div>
          <span
            className="flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full capitalize"
            style={statusStyle(competition.status)}
          >
            {competition.status}
          </span>
        </div>

        {/* Organization */}
        <div className="flex items-center gap-1.5 mb-3">
          <Building className="w-3.5 h-3.5 flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <span className="text-xs truncate" style={{ color: SYSTEM_COLORS.textMuted }}>
            {competition.organization}
          </span>
        </div>

        {/* Description */}
        <p
          className="text-sm leading-relaxed line-clamp-2 mb-4 flex-1"
          style={{ color: SYSTEM_COLORS.textMuted }}
        >
          {competition.description}
        </p>

        {/* Funding */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
          style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}
        >
          <DollarSign className="w-4 h-4 flex-shrink-0" style={{ color: SYSTEM_COLORS.success }} />
          <span className="text-sm font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
            {competition.currency} {Number(competition.fundingAmount ?? 0).toLocaleString()}
          </span>
        </div>

        {/* Deadline */}
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: SYSTEM_COLORS.textSoft }} />
          <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
            {competition.applicationDeadline
              ? format(new Date(competition.applicationDeadline), 'MMM d, yyyy')
              : '—'}
          </span>
          <span
            className="ml-auto flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{ backgroundColor: dl.bg, color: dl.color }}
          >
            {dl.label}
          </span>
        </div>

        {/* Requirements */}
        {competition.requirements?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {competition.requirements.slice(0, 3).map((req, i) => (
              <span
                key={i}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted, color: SYSTEM_COLORS.textMuted }}
              >
                {req}
              </span>
            ))}
            {competition.requirements.length > 3 && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted, color: SYSTEM_COLORS.textMuted }}
              >
                +{competition.requirements.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-3 border-t" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          {canCreateApplication && competition.status === 'open' && (
            <button
              onClick={() => onCreateApplication(competition)}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
            >
              <Plus size={15} />
              Apply
            </button>
          )}
          {canEdit && (
            <button
              onClick={() => onEdit(competition)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition-colors hover:opacity-80"
              style={{
                borderColor: SYSTEM_COLORS.border,
                color: SYSTEM_COLORS.textMuted,
                backgroundColor: SYSTEM_COLORS.shellSurface,
              }}
            >
              <Edit size={14} />
              Edit
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetitionCard;
